import { type FC, type PropsWithChildren, useEffect } from 'react'

import { Body, Heading } from '@foxino/components-web'

import { ImageContent } from '@app/config/constants/ImageContent'
import { useTranslation } from '@app/locales'

import { useCheckMaintenance } from '../../hooks/useCheckMaintenance'
import { sideNavigationStore } from '../../store/sideNavigationStore'

const CHECK_INTERVAL = 20_000

export const MaintenanceProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation('maintenance')
  const { isMaintenance, checkMaintenanceStatus } = useCheckMaintenance()

  useEffect(() => {
    if (isMaintenance) {
      sideNavigationStore.setIsOpen(false)
    } else if (isMaintenance === false) {
      sideNavigationStore.setIsOpen(true)
    }
  }, [isMaintenance])

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null

    if (isMaintenance === false) {
      if (intervalId) {
        clearInterval(intervalId)
      }
    } else if (isMaintenance) {
      intervalId = setInterval(() => {
        checkMaintenanceStatus()
      }, CHECK_INTERVAL)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isMaintenance])

  if (isMaintenance === null) {
    return null
  } else if (isMaintenance === false) {
    return children
  }

  return (
    <div className="absolute inset-0 flex flex-col overflow-auto overscroll-none bg-[radial-gradient(100%_256%_at_100%_48%,#1D1B46_0%,#14122C_100%)]">
      <div className="mx-auto flex w-full max-w-7xl flex-1 shrink-0 flex-col p-4">
        <img src={ImageContent.maintenance.logo} alt={t('logoAlt')} className="hidden w-24 md:block" />

        <div className="flex flex-1 flex-col items-center justify-center gap-4 md:mt-8 md:flex-row md:justify-between md:gap-8">
          <div className="flex flex-col items-center font-bold md:flex-1">
            <div className="rounded-md bg-[linear-gradient(135deg,rgba(0,0,0,.15)_25%,transparent_25%,transparent_50%,rgba(0,0,0,.15)_50%,rgba(0,0,0,.15)_75%,transparent_75%,transparent_100%),linear-gradient(180deg,#25B156)] bg-[length:2rem_2rem] px-2 py-1 uppercase">
              {t('titleBadge')}
            </div>

            <Heading size="lg" className="my-4 max-w-[30ch] text-balance text-center text-white">
              {t('v2.title')}
            </Heading>

            <Body className="my-1 whitespace-pre-line text-pretty text-center text-[#C3BDFF]">{t('v2.message')}</Body>

            {/*
              <Heading size="l" className="my-4 max-w-[30ch] text-balance">
                {t('v1.title')}
              </Heading>
            */}

            {/*messages.map((paragraph, index) => {
              if (Array.isArray(paragraph)) {
                return (
                  <ul key={index} className="list-disc pl-8">
                    {paragraph.map((item, i) => (
                      <Body key={`${index}-${i}`} Component="li" className="whitespace-pre-line text-pretty text-[#C3BDFF]">
                        {item}
                      </Body>
                    ))}
                  </ul>
                )
              }

              return (
                <Body key={index} className="my-1 whitespace-pre-line text-pretty text-[#C3BDFF]">
                  {paragraph}
                </Body>
              )
            })*/}
          </div>

          <img
            srcSet={`${ImageContent.maintenance.foxino_640w} 640w, ${ImageContent.maintenance.foxino_768w} 768w, ${ImageContent.maintenance.foxino_1024w} 1024w`}
            sizes="(width <= 640px) 640px, (width <= 768px) 768px, 1024px"
            src={ImageContent.maintenance.foxino_768w}
            alt={t('foxinoAlt')}
            className="mx-auto w-60 shrink-0 md:mr-32 md:w-80 lg:w-96"
          />
        </div>
      </div>
    </div>
  )
}
